import React, { useEffect, useState } from "react";

import { GeographyType } from "models/Geography";
import { useLocation, useParams } from "react-router-dom";
import FormsLibrarySearch from "../../shared/forms/library/FormsLibrarySearch";
import { AtticusForm } from "models/AtticusForm";
import { QueryPayload } from "apis/FormsAPI";
import { formsApi } from "apis";
import { Box, Typography } from "@mui/material";
import { FormsList } from "../../shared/forms/list/FormsList";
import { FormsListZeroState } from "../../shared/forms/list/FormsListZeroState";
import { useAuthContext } from "AuthContextWrapper"
import FormsFilesHeader from "../FormsFilesHeader";
import { useNavigate } from "react-router-dom";

const FormsLibraryEstateView = (): JSX.Element => {
  const navigate = useNavigate();
  const { user, activeEstate, formTypes, languages } = useAuthContext()
  const { autoloadFormId } = useParams()
  const { state: locationState } = useLocation();
  const initQueryParams = locationState as QueryPayload || undefined
  const [forms, setForms] = useState<AtticusForm[] | undefined>(undefined);
  const [loadingForms, setLoadingForms] = useState(false);
  const [autoloadForm, setAutoloadForm] = useState<AtticusForm>();

  const hasAccess = !user?.advisorGroup?.formsDisabled;

  const getForms = async (queryPayload: QueryPayload): Promise<void> => {
    setLoadingForms(true);

    if (!activeEstate?.id) {
      throw "No estate ID";
    }

    const newForms = await formsApi.fetchForms(activeEstate.id, queryPayload);

    // Auto-open a form in the forms dialog if its gov id was provided in the url
    const newAutoloadForm = autoloadFormId ? newForms?.find(f => f.governmentId === autoloadFormId || f.formGovernmentId === autoloadFormId) : undefined
    setAutoloadForm(newAutoloadForm)

    // Filter forms by language
    const allowedLanguages = ["en", "fr"];
    const filteredForms = newForms?.filter(
      (form) =>
        (form.language?.code &&
          allowedLanguages.includes(form.language?.code)) ||
        null
    );
    if (filteredForms) {
      setForms(filteredForms);
    }

    setLoadingForms(false);
  };

  useEffect(() => {
    if (!activeEstate) {
      return
    }

    if (initQueryParams) {
      getForms(initQueryParams);
    } else if (autoloadFormId) {
      getForms({
        search_terms: autoloadFormId
      })
    } else {
      getForms({
        geography_id: activeEstate.stateId || undefined,
        geography_type: GeographyType.State,
      })
    }
  }, [activeEstate]);

  return hasAccess ? (
    <div data-automation-id="page-content-estate-forms-library">
      {activeEstate && (
        <>
          <FormsFilesHeader
            title="Atticus Forms Library"
            description="Looking for other forms? Search our Forms Library to find or use more than 3,500 forms. Start by searching for a form, choosing a location, or both:"
            backButtonOptions={{
              text: "Back to My Forms",
              onClick: () => {
                navigate(`/app/forms-and-files`);
              },
            }}
          />
          <Box mt={3} mb={3}>
            <FormsLibrarySearch
              hideHeader
              initGeographyType={
                initQueryParams?.geography_type && initQueryParams?.geography_id
                  ? initQueryParams?.geography_type
                  : GeographyType.State
              }
              initGeographyId={
                initQueryParams?.geography_type && initQueryParams?.geography_id
                  ? initQueryParams?.geography_id || undefined
                  : activeEstate.stateId || undefined
              }
              geographyInclusive={initQueryParams?.geography_inclusive}
              initLanguageCode={
                languages?.find(
                  (lang) => lang.id === initQueryParams?.language_id
                )?.code
              }
              initFormTypeSlug={
                formTypes?.find((ft) => ft.id === initQueryParams?.form_type_id)
                  ?.slug
              }
              initSearchTerms={initQueryParams?.search_terms}
              onComplete={getForms}
              loadingForms={loadingForms}
            />
          </Box>
          <Box>
            <FormsList
              key={"forms list" + autoloadForm?.id}
              forms={forms}
              autoloadForm={autoloadForm}
              estate={activeEstate}
              emptyState={<FormsListZeroState />}
              allowCheckedBadge
            />
          </Box>
        </>
      )}
    </div>
  ) : (
    <Typography variant="body1">We can't seem to recognize your account.  Please try logging out and back in again.</Typography>
  );
};

export default FormsLibraryEstateView;
